import type { StudyMissionSelectDto, StudySwimlaneSelectDto, StudyTaskReadinessSelectDto } from '@@types/api.types';
import type { MeiaPhaseKey } from '../meia.constants';
import {
  getMeiaMissionAreaOfOperations,
  getMeiaMissionCommandersIntent,
  getMeiaMissionDesiredOutcome,
  getMeiaMissionTimeWindow,
} from './meia-mission-display.util';
import {
  type MeiaTaskReadinessStatus,
  combineMeiaReadinessStatuses,
  getMeiaSwimlaneMeasuresCollectionStatus,
  getMeiaSwimlaneTaskCollectionStatus,
} from './meia-task-column.util';

/**
 * Centralized readiness logic for the MEIA red/yellow/green icons. The same status union and
 * `combineMeiaReadinessStatuses` rollup (empty > partial > complete) flow up a waterfall:
 * Task → Stage → Mission Thread → Mission. Mission readiness is phase-aware (P1 = framing only;
 * P2+ also requires the mission's threads to pass). The icon itself lives in
 * `meia-task-readiness-status-icon.component.tsx` — never duplicate the icon or these rules.
 */

/** Mission Thread readiness: every stage has MoS/MoE coverage and complete tasks. */
export const getMeiaThreadReadinessStatus = (params: {
  swimlanes: Pick<StudySwimlaneSelectDto, 'id' | 'measuresStatus'>[];
  taskReadiness: StudyTaskReadinessSelectDto[];
}): MeiaTaskReadinessStatus =>
  combineMeiaReadinessStatuses([
    getMeiaSwimlaneMeasuresCollectionStatus(params.swimlanes),
    getMeiaSwimlaneTaskCollectionStatus({ swimlanes: params.swimlanes, taskReadiness: params.taskReadiness }),
  ]);

export const getMeiaThreadReadinessTooltip = (status: MeiaTaskReadinessStatus): string => {
  switch (status) {
    case 'complete':
      return 'Every Stage in this Mission Thread has MoS/MoE coverage and complete Tasks.';
    case 'partial':
      return 'This Mission Thread has incomplete Stage MoS/MoE coverage or incomplete Tasks.';
    case 'empty':
      return 'This Mission Thread has a Stage missing MoS coverage or Tasks.';
  }
};

/** Mission framing readiness (P1): the four core framing fields are filled in. */
export const getMeiaMissionFramingReadinessStatus = (mission: StudyMissionSelectDto): MeiaTaskReadinessStatus => {
  const framingChecks = [
    getMeiaMissionDesiredOutcome(mission),
    getMeiaMissionCommandersIntent(mission),
    getMeiaMissionAreaOfOperations(mission),
    getMeiaMissionTimeWindow(mission),
  ].map((value) => value.trim().length > 0);
  const filled = framingChecks.filter(Boolean).length;

  if (filled === framingChecks.length) return 'complete';
  if (filled === 0) return 'empty';
  return 'partial';
};

/**
 * Phase-aware mission readiness. On P1 (Mission Framing) only the mission's own framing matters.
 * From P2 onward it waterfalls: the framing status is combined with the mission's threads — no
 * threads reads as `empty`, otherwise the worst-case thread readiness rolls up into the mission.
 */
export const getMeiaMissionReadinessStatus = (params: {
  mission: StudyMissionSelectDto;
  phase: MeiaPhaseKey;
  threadStatuses?: MeiaTaskReadinessStatus[];
}): MeiaTaskReadinessStatus => {
  const { mission, phase, threadStatuses } = params;
  const framing = getMeiaMissionFramingReadinessStatus(mission);
  if (phase === 'p1') return framing;

  const threads = threadStatuses ?? [];
  const threadsStatus: MeiaTaskReadinessStatus = threads.length === 0 ? 'empty' : combineMeiaReadinessStatuses(threads);
  return combineMeiaReadinessStatuses([framing, threadsStatus]);
};

export const getMeiaMissionReadinessTooltip = (status: MeiaTaskReadinessStatus, phase: MeiaPhaseKey): string => {
  if (phase === 'p1') {
    switch (status) {
      case 'complete':
        return 'Mission framing is complete — DMO, intent, area of operations, and time window are all set.';
      case 'partial':
        return 'Mission framing is incomplete — finish the DMO, intent, area of operations, and time window in Mission Framing.';
      case 'empty':
        return 'This mission is not framed yet — add its DMO, intent, area of operations, and time window in Mission Framing.';
    }
  }

  switch (status) {
    case 'complete':
      return 'Mission is ready — framing is complete and every mission thread passes its checks.';
    case 'partial':
      return 'Mission needs work — finish the framing or complete its mission threads (stages, measures, and tasks).';
    case 'empty':
      return 'Mission is not ready — add its framing and at least one mission thread.';
  }
};
